<template>
  <v-container fluid class="pa-0">
    <StatsBar
      :total="filteredAlerts.length"
      :unique-types="uniqueTypes"
      :unique-statuses="uniqueStatuses"
      :average-message-rate="averageMessageRate"
    />
    <v-card elevation="0" rounded class="alerts-table-card" color="surface">
      <!-- Header with title and count -->
      <v-card-title
        class="table-header"
        :style="{
          direction: $i18n.locale === 'he' ? 'rtl' : 'ltr',
        }"
      >
        <SearchBar
          :add-to-label="`(${filteredAlerts.length}/${alerts.length})`"
          :model-value="search"
          @update:model-value="$emit('update:search', $event)"
          class="mx-4"
        />
      </v-card-title>

      <!-- Advanced Filters -->
      <div
        class="filters-container"
        :style="{
          direction: $i18n.locale === 'he' ? 'rtl' : 'ltr',
        }"
      >
        <AdvancedFilters
          :filters="filters"
          :status-options="statusOptions"
          :type-options="typeOptions"
          :severity-options="severityOptions"
          :layer-options="layerOptions"
          @update:filters="handleFiltersUpdate"
          @clear-filters="clearAllFilters"
        />
      </div>

      <v-divider></v-divider>

      <AlertsDataTable
        :headers="headers"
        :filtered-alerts="filteredAlerts"
        :search="search"
        :loading="loading"
        @update:search="$emit('update:search', $event)"
      />
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import StatsBar from "../common/StatsBar.vue";
import AdvancedFilters from "./AdvancedFilters.vue";
import AlertsDataTable from "./AlertsDataTable.vue";
import SearchBar from "../ui/SearchBar.vue";
import { flattenCapAlert } from "../../utils/alertUtils.js";
import { getTableHeaders } from "../../config/tableHeaders.js";

export default {
  name: "AlertsTab",
  components: {
    StatsBar,
    AdvancedFilters,
    AlertsDataTable,
    SearchBar,
  },
  props: {
    search: {
      type: String,
      default: "",
    },
    filters: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:search", "update:filters", "clear-filters"],
  computed: {
    ...mapGetters(["alerts"]),
    ...mapState(["averageMessageRate"]),
    currentLang() {
      return this.$i18n.locale || "en";
    },
    headers() {
      return getTableHeaders(this.$t);
    },
    flattenedAlerts() {
      return this.alerts.map(flattenCapAlert);
    },
    filteredAlerts() {
      let filtered = this.flattenedAlerts;

      if (this.filters.status) {
        filtered = filtered.filter((a) => a.status === this.filters.status);
      }
      if (this.filters.type) {
        filtered = filtered.filter((a) => a.msgType === this.filters.type);
      }
      if (this.filters.severity) {
        filtered = filtered.filter((a) => a.severity === this.filters.severity);
      }
      if (this.filters.layer) {
        filtered = filtered.filter(
          (a) => a.layers && a.layers.includes(this.filters.layer)
        );
      }

      if (!this.search) return filtered;

      const searchLower = this.search.toLowerCase();

      return filtered.filter((a) => {
        const headline = this.getHeadline(a);
        return (
          (headline && headline.toLowerCase().includes(searchLower)) ||
          (a.identifier && a.identifier.toLowerCase().includes(searchLower)) ||
          (a.sender && a.sender.toLowerCase().includes(searchLower)) ||
          (a.layers &&
            a.layers.some((layer) =>
              layer.toLowerCase().includes(searchLower)
            )) ||
          (a.severity && a.severity.toLowerCase().includes(searchLower))
        );
      });
    },
    uniqueTypes() {
      const types = new Set(this.filteredAlerts.map((a) => a.msgType));
      return types.size;
    },
    uniqueStatuses() {
      const statuses = new Set(this.filteredAlerts.map((a) => a.status));
      return statuses.size;
    },
    statusOptions() {
      const statuses = new Set(this.alerts.map((a) => a.status));
      return Array.from(statuses).map((status) => ({
        title: this.$t(status ? status.toLowerCase() : "unknown"),
        value: status,
      }));
    },
    typeOptions() {
      const types = new Set(this.alerts.map((a) => a.msgType));
      return Array.from(types).map((type) => ({
        title: type,
        value: type,
      }));
    },
    severityOptions() {
      const severities = new Set(this.alerts.map((a) => a.severity || ""));
      return Array.from(severities).map((severity) => ({
        title: this.$t(severity ? severity.toLowerCase() : "unknown"),
        value: severity,
      }));
    },
    layerOptions() {
      const layersSet = new Set(this.alerts.flatMap((a) => a.layers || []));
      return Array.from(layersSet).map((layer) => ({
        title: layer ? this.$t(layer) : "",
        value: layer,
      }));
    },
  },
  methods: {
    clearAllFilters() {
      this.$emit("clear-filters");
    },
    handleFiltersUpdate(newFilters) {
      this.$emit("update:filters", newFilters);
    },
    getHeadline(alert) {
      if (!alert.headlines) return null;
      return (
        alert.headlines[this.currentLang] ||
        Object.values(alert.headlines)[0] ||
        null
      );
    },
    getDescription(alert) {
      if (!alert.descriptions) return null;
      return (
        alert.descriptions[this.currentLang] ||
        Object.values(alert.descriptions)[0] ||
        null
      );
    },
    getLayer(alert) {
      return alert.layers && alert.layers.length > 0 ? alert.layers[0] : null;
    },
  },
};
</script>

<style scoped>
.alerts-table-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.table-header {
  background: var(--v-theme-surface-variant);
  border-radius: 8px 8px 0 0;
  padding: 16px;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 70%;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.table-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--v-theme-primary);
}

.v-theme--dark .table-title {
  color: #ffffff !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.results-count {
  font-size: 0.9rem;
  color: var(--v-theme-secondary);
  font-weight: 500;
}

.filters-container {
  padding: 0 16px;
}

[dir="rtl"] .header-left {
  flex-direction: row-reverse;
}

[dir="rtl"] .header-right {
  justify-content: flex-start;
}

[dir="rtl"] .filters-container {
  text-align: right;
}

@media (max-width: 768px) {
  .alerts-table-card {
    padding: 0.5em 0.2em;
    overflow-x: auto;
    padding-left: 5%;
    padding-right: 7%;
  }
  .table-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.5em;
    padding: 0.5em 0.2em;
  }
  .header-left {
    margin-bottom: 0.5em;
  }
  .filters-container {
    flex-direction: column !important;
    gap: 0.5em;
    padding: 0.5em 0.2em;
  }
}
</style>
